// Assign-mode centre preview for the Model Solution view. Shows the uploaded
// full solution (original upload or the digitized OCR copy, toggled by the
// folded-corner button) and lets Kim highlight passages with a text-marker:
// click-drag selects (green), Ctrl/Cmd-drag adds another passage, clicking the
// paper clears. Clicking a task card then assigns the selection (handled in
// ModelSolutionPanel, which reads the pending portions from the store).
//
// Selection only works on text — the digital upload or the digitized copy. A
// raw scan (original view, no text layer) has nothing to select; the panel's
// status line nudges Kim to run OCR.

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { useTranslation } from "react-i18next";

import type { Project } from "../api/types";
import { useModelUiStore, type SelectionPortion } from "../state/modelUiStore";

import "react-pdf/dist/esm/Page/TextLayer.css";
import "./ModelAssignPreview.css";

function IconPage() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M2 1.5h5L9.5 5v6.5a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" />
      <path d="M7 1.5V5h2.5" />
    </svg>
  );
}

export function ModelAssignPreview({ project }: { project: Project }) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);

  const modelView = useModelUiStore((s) => s.modelView);
  const setModelView = useModelUiStore((s) => s.setModelView);
  const portions = useModelUiStore((s) => s.portions);
  const addPortion = useModelUiStore((s) => s.addPortion);
  const replacePortions = useModelUiStore((s) => s.replacePortions);
  const clearPortions = useModelUiStore((s) => s.clearPortions);

  const hasDigitized = !!project.model_full_digitized_pdf_path;
  const showDigitized = hasDigitized && modelView === "digitized";
  // Selection needs a text layer: the digitized copy, or a digital original.
  const selectable = showDigitized || (!showDigitized && !!(project.model_full_text ?? "").trim());

  const url = showDigitized
    ? `/api/projects/${project.id}/model_full_digitized_pdf`
    : `/api/projects/${project.id}/model_full_pdf`;
  const file = useMemo(() => ({ url, withCredentials: true }), [url]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Capture a selection on mouse-up: store the text + page-relative rectangles
  // so we can draw the green overlay and (on task click) assign the text.
  function onMouseUp(e: React.MouseEvent) {
    if (!selectable) return;
    const sel = window.getSelection();
    const text = sel ? sel.toString() : "";
    if (!sel || !text.trim()) {
      clearPortions(); // a plain click clears the highlight
      return;
    }
    const pageEls = Array.from(
      pagesRef.current?.querySelectorAll<HTMLElement>("[data-page]") ?? [],
    );
    const rects: SelectionPortion["rects"] = [];
    for (let r = 0; r < sel.rangeCount; r++) {
      for (const cr of Array.from(sel.getRangeAt(r).getClientRects())) {
        if (cr.width < 1 || cr.height < 1) continue;
        const cx = cr.left + cr.width / 2;
        const cy = cr.top + cr.height / 2;
        const pageEl = pageEls.find((el) => {
          const b = el.getBoundingClientRect();
          return cx >= b.left && cx <= b.right && cy >= b.top && cy <= b.bottom;
        });
        if (!pageEl) continue;
        const b = pageEl.getBoundingClientRect();
        const page = Number(pageEl.dataset.page);
        rects.push({
          page,
          box: [
            (cr.left - b.left) / b.width,
            (cr.top - b.top) / b.height,
            (cr.right - b.left) / b.width,
            (cr.bottom - b.top) / b.height,
          ],
        });
      }
    }
    if (!rects.length) return;
    const portion: SelectionPortion = { text, rects };
    // Ctrl/Cmd extends the selection with another passage (Chrome can't hold
    // multiple native ranges, so we accumulate them ourselves); a plain drag
    // replaces. Clear the native selection — our overlays are the source of truth.
    if (e.ctrlKey || e.metaKey) addPortion(portion);
    else replacePortions([portion]);
    sel.removeAllRanges();
  }

  return (
    <div className="nav-main__content model-assign">
      <div className="nav-main__preview-header">
        <span className="nav-main__section-title">
          {showDigitized
            ? t("model_solution_panel.assign_preview_digitized")
            : t("model_solution_panel.assign_preview")}
        </span>
        <div className="nav-main__preview-actions">
          {hasDigitized && (
            <button
              type="button"
              className={"nav-main__doc-toggle" + (showDigitized ? " nav-main__doc-toggle--active" : "")}
              onClick={() => setModelView(showDigitized ? "original" : "digitized")}
              title={t(showDigitized ? "exam_panel.view_original" : "exam_panel.view_digitized")}
              aria-label={t(showDigitized ? "exam_panel.view_original" : "exam_panel.view_digitized")}
              aria-pressed={showDigitized}
            >
              <IconPage />
            </button>
          )}
        </div>
      </div>

      <div className="model-assign__hint">
        {selectable ? t("model_solution_panel.assign_hint") : t("model_solution_panel.assign_needs_text")}
      </div>

      <div className="model-assign__scroll" ref={wrapRef}>
        <div
          className={"model-assign__pages" + (selectable ? " model-assign__pages--selectable" : "")}
          ref={pagesRef}
          onMouseUp={onMouseUp}
        >
          {width > 0 && (
            <Document
              file={file}
              loading={<div className="nav-main__pdf-skeleton" style={{ width, aspectRatio: "0.707" }} />}
              noData={<div className="nav-main__pdf-skeleton" style={{ width, aspectRatio: "0.707" }} />}
              error={null}
              onLoadSuccess={(pdf: { numPages: number }) => setNumPages(pdf.numPages)}
            >
              {Array.from({ length: numPages }, (_, i) => {
                const p = i + 1;
                const marks = portions.flatMap((portion) =>
                  portion.rects.filter((rc) => rc.page === p),
                );
                return (
                  <div key={p} className="nav-main__pdf-page" data-page={p}>
                    <Page
                      pageNumber={p}
                      width={width}
                      renderTextLayer={selectable}
                      renderAnnotationLayer={false}
                      loading={<div className="nav-main__pdf-skeleton" style={{ width, aspectRatio: "0.707" }} />}
                    />
                    {marks.map((rc, idx) => (
                      <div
                        key={idx}
                        className="model-assign__mark"
                        style={{
                          left: `${rc.box[0] * 100}%`,
                          top: `${rc.box[1] * 100}%`,
                          width: `${(rc.box[2] - rc.box[0]) * 100}%`,
                          height: `${(rc.box[3] - rc.box[1]) * 100}%`,
                        }}
                        aria-hidden
                      />
                    ))}
                  </div>
                );
              })}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}
